import { Button, DrinkIcon, formatBaht, Icons, StateScreen } from "@jod/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { customerBackendApi } from "../../features/api/backend.ts";
import { t, useT } from "../../features/i18n/index.ts";
import type { InboxItem } from "../../features/inbox/inbox.ts";
import { useSession } from "../../features/session/session-context.tsx";

export const Route = createFileRoute("/_tabs/gifts/incoming")({
  component: IncomingScreen,
});

function IncomingScreen() {
  const { backendMode } = useSession();
  return backendMode ? <BackendIncoming /> : <MockIncoming />;
}

/** Backend-mode inbox: live gifts from GET /gifts/incoming, accept/reject/block. */
function BackendIncoming() {
  const t = useT();
  const { respondGift, reportTable, toast } = useSession();
  const giftsQuery = useQuery({
    queryKey: ["customer", "gifts", "incoming"],
    queryFn: () =>
      customerBackendApi?.listIncomingGifts() ??
      Promise.reject(new Error("Customer backend API unavailable")),
    refetchInterval: 8_000,
  });
  const gifts = (giftsQuery.data ?? []).filter((g) => g.status === "pending");

  const respond = async (
    giftId: string,
    action: "accept" | "reject" | "block",
    senderPartyId: string,
    okText: string,
  ) => {
    try {
      await respondGift(giftId, action, senderPartyId);
      toast(okText);
      await giftsQuery.refetch();
    } catch {
      // respondGift already surfaced a toast on failure.
    }
  };

  if (gifts.length === 0) {
    return (
      <div className="h-[70vh]">
        <StateScreen
          title={giftsQuery.isLoading ? t("inbox.loadingTitle") : t("inbox.emptyTitle")}
          body={giftsQuery.isError ? t("inbox.loadError") : t("inbox.emptyBody")}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <div className="jod-kicker">{t("inbox.kicker")}</div>
        <h1 className="h-display text-[1.5rem]">{t("inbox.title")}</h1>
        <p className="text-cream-3 text-sm">{t("inbox.subtitle")}</p>
      </div>

      <div className="space-y-3">
        {gifts.map((gift) => (
          <div key={gift.id} className="glass-hi relative overflow-hidden rounded-xl p-4">
            <div className="jod-sheen" aria-hidden="true" />
            <div className="relative flex items-center gap-3.5">
              <span className="grid size-12 shrink-0 place-items-center rounded-lg bg-[oklch(100%_0_0_/_5%)] text-gold">
                <DrinkIcon name="IconCocktail" size={26} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="font-display font-semibold text-cream">
                  {t("inbox.sentToYou", {
                    table: gift.senderTableCode ?? gift.senderTableLabel ?? t("inbox.unknownTable"),
                  })}
                </div>
                <div className="truncate text-cream-3 text-xs">
                  {gift.itemName ?? "Just One Drink"}
                  {gift.totalSatang != null ? ` · ${formatBaht(gift.totalSatang)}` : ""}
                </div>
              </div>
              <span className="rounded-full border border-hairline px-2.5 py-1 text-[0.65rem] text-gold">
                {gift.status}
              </span>
            </div>

            {gift.message ? (
              <p className="relative mt-3 rounded-md bg-[oklch(100%_0_0_/_4%)] px-3.5 py-2.5 text-cream-2 text-sm">
                “{gift.message}”
              </p>
            ) : null}

            <p className="relative mt-3 text-cream-3 text-xs">
              {t("inbox.giftAcceptNote")}
              {gift.requestedContact ? ` · ${t("inbox.igAttached")}` : ""}
            </p>

            <div className="relative mt-3.5 flex gap-2.5">
              <Button
                size="sm"
                variant="accent"
                className="flex-1"
                onClick={() =>
                  respond(gift.id, "accept", gift.senderPartyId, t("inbox.acceptGiftToast"))
                }
              >
                {t("inbox.acceptGift")}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  respond(gift.id, "reject", gift.senderPartyId, t("inbox.declinedToast"))
                }
              >
                {t("inbox.declineSoft")}
              </Button>
              <Button
                size="sm"
                variant="destructive"
                aria-label="Block"
                onClick={() => respond(gift.id, "block", gift.senderPartyId, t("inbox.blockToast"))}
              >
                <Icons.IconShield size={16} />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                aria-label="Report"
                onClick={() => {
                  reportTable(
                    gift.senderTableCode ?? gift.senderTableLabel ?? "unknown",
                    "guest_report",
                    gift.senderPartyId,
                  );
                  toast(t("inbox.reportToast"));
                }}
              >
                <Icons.IconFlag size={16} />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Mock-mode inbox: local store + the staff fulfil/reject simulation. */
function MockIncoming() {
  const t = useT();
  const { inbox, respondInbox, toast } = useSession();
  const active = inbox.filter((i) => !["declined", "expired"].includes(i.state));

  if (active.length === 0) {
    return (
      <div className="h-[70vh]">
        <StateScreen title={t("inbox.emptyTitle")} body={t("inbox.emptyBody")} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <div className="jod-kicker">{t("inbox.kicker")}</div>
        <h1 className="h-display text-[1.5rem]">{t("inbox.title")}</h1>
        <p className="text-cream-3 text-sm">{t("inbox.subtitle")}</p>
      </div>

      <div className="space-y-3">
        {active.map((item) => (
          <InboxCard
            key={item.id}
            item={item}
            onAccept={() => {
              respondInbox(item.id, "accept");
              toast(
                item.kind === "gift" ? t("inbox.acceptGiftToast") : t("inbox.acceptContactToast"),
              );
            }}
            onDecline={() => respondInbox(item.id, "decline")}
            onBlock={() => {
              respondInbox(item.id, "block");
              toast(t("inbox.blockToast"));
            }}
            onFulfill={() => {
              respondInbox(item.id, "fulfill");
              toast(t("inbox.fulfillToast"));
            }}
            onStaffReject={() => {
              respondInbox(item.id, "staff_reject");
              toast(t("inbox.staffRejectToast"));
            }}
          />
        ))}
      </div>
    </div>
  );
}

interface InboxCardProps {
  item: InboxItem;
  onAccept: () => void;
  onDecline: () => void;
  onBlock: () => void;
  onFulfill: () => void;
  onStaffReject: () => void;
}

function InboxCard({
  item,
  onAccept,
  onDecline,
  onBlock,
  onFulfill,
  onStaffReject,
}: InboxCardProps) {
  const t = useT();
  const isGift = item.kind === "gift";
  const pending = item.state === "pending";
  const accepted = item.state === "accepted";
  const finalCopy = getFinalCopy(item);

  return (
    <div className="glass-hi relative overflow-hidden rounded-xl p-4">
      <div className="jod-sheen" aria-hidden="true" />
      <div className="relative flex items-center gap-3.5">
        <span className="grid size-12 shrink-0 place-items-center rounded-lg bg-[oklch(100%_0_0_/_5%)] text-gold">
          {isGift ? (
            <DrinkIcon name={item.iconName ?? "IconCocktail"} size={26} />
          ) : (
            <Icons.IconIG size={24} />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-display font-semibold text-cream">
            {isGift
              ? t("inbox.sentToYou", { table: item.fromTable })
              : t("inbox.greetsYou", { table: item.fromTable })}
          </div>
          <div className="truncate text-cream-3 text-xs">
            {isGift
              ? (item.subjectTh ?? item.subject)
              : t("inbox.requestSubject", { subject: item.subject })}
          </div>
        </div>
        <span className="rounded-full border border-hairline px-2.5 py-1 text-[0.65rem] text-gold">
          {item.state}
        </span>
      </div>

      {item.message ? (
        <p className="relative mt-3 rounded-md bg-[oklch(100%_0_0_/_4%)] px-3.5 py-2.5 text-cream-2 text-sm">
          “{item.message}”
        </p>
      ) : null}

      <p className="relative mt-3 text-cream-3 text-xs">
        {isGift ? t("inbox.giftAcceptNote") : t("inbox.contactNote")}
      </p>

      {finalCopy ? (
        <div className="relative mt-3 flex items-center gap-2 font-medium text-cream-2 text-sm">
          <Icons.IconCheck size={16} /> {finalCopy}
        </div>
      ) : null}

      {accepted ? (
        <div className="relative mt-3 space-y-3">
          <div className="flex items-center gap-2 font-medium text-lime text-sm">
            <Icons.IconCheck size={16} /> {t("inbox.acceptedAwaitStaff")}
          </div>
          {isGift ? (
            <div className="flex gap-2.5">
              <Button size="sm" variant="accent" className="flex-1" onClick={onFulfill}>
                Staff fulfil
              </Button>
              <Button size="sm" variant="destructive" onClick={onStaffReject}>
                Staff reject/refund
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      {pending ? (
        <div className="relative mt-3.5 flex gap-2.5">
          <Button size="sm" variant="accent" className="flex-1" onClick={onAccept}>
            {isGift ? t("inbox.acceptGift") : t("inbox.acceptContact")}
          </Button>
          <Button size="sm" variant="ghost" onClick={onDecline}>
            {t("inbox.declineSoft")}
          </Button>
          <Button size="sm" variant="destructive" onClick={onBlock} aria-label="Block">
            <Icons.IconShield size={16} />
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function getFinalCopy(item: InboxItem): string | null {
  switch (item.state) {
    case "fulfilled":
      return t("order.step.served");
    case "staff_rejected":
      return t("inbox.finalStaffRejected");
    case "declined":
      return t("inbox.finalDeclined");
    case "expired":
      return t("inbox.finalExpired");
    default:
      return null;
  }
}
